import { useState, useEffect, useCallback, useRef } from 'react';
import { useData } from '../../../contexts/DataContext';
import { ScanService } from '../../../services';
import { useAccountsByClient } from '../../../services/queries';

/**
 * Custom hook for managing scan creation form state and logic
 * 
 * Provides form state management, validation, and API integration
 * for creating new scans with proper client/account selection.
 * 
 * @param {Object} initialData - Initial form data
 * @returns {Object} Form state, handlers, and computed values
 */
export const useScanCreate = (initialData = {}) => {
	// Data context for clients and accounts
	const { clients } = useData();

	// Calculate default dates for 30 days preset
	const getDefaultDates = () => {
		const endDate = new Date();
		const startDate = new Date();
		startDate.setDate(endDate.getDate() - 30);
		return {
			startDate: startDate.toISOString().split('T')[0],
			endDate: endDate.toISOString().split('T')[0]
		};
	};

	const defaultDates = getDefaultDates();

	// Form state
	const [formData, setFormData] = useState({
		platform: initialData.platform || "x",
		username: initialData.username || "",
		startDate: initialData.startDate || defaultDates.startDate,
		endDate: initialData.endDate || defaultDates.endDate,
		datePreset: initialData.datePreset || "30days",
		customKeywords: initialData.customKeywords || [],
		sensitivityLevel: initialData.sensitivityLevel || "medium",
		enabledCategories: initialData.enabledCategories || [
			"hate_speech",
			"harassment",
			"explicit",
			"violence",
			"political",
			"cultural",
			"misconduct",
			"drug_use",
			"self_harm",
			"spam",
			"other",
		],
	});

	// Selection state
	const [selectedClientId, setSelectedClientId] = useState(initialData.clientId || null);
	const [selectedAccountId, setSelectedAccountId] = useState(initialData.accountId || null);

	// UI state
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [validationErrors, setValidationErrors] = useState([]);

	// Accounts for the selected client, from the shared query cache - the same
	// data Monitoring's picker uses, fetched once and reused.
	const { data: availableAccounts = [], isFetching: accountsLoading } =
		useAccountsByClient(selectedClientId);

	// Selecting a DIFFERENT client invalidates the previous account choice.
	// Guarded against the mount run: the form can arrive pre-filled (Account
	// card -> "Scan" passes clientId + accountId), and clearing on mount would
	// wipe that prefill.
	const prevClientRef = useRef(selectedClientId);
	useEffect(() => {
		if (prevClientRef.current !== selectedClientId) {
			prevClientRef.current = selectedClientId;
			setSelectedAccountId(null);
		}
	}, [selectedClientId]);

	// Validate selected account when accounts change
	useEffect(() => {
		if (selectedAccountId && availableAccounts.length > 0) {
			const accountExists = availableAccounts.find(acc => acc._id === selectedAccountId);
			if (!accountExists) {
				// Clear selection if account no longer exists
				setSelectedAccountId(null);
				setFormData(prev => ({ ...prev, platform: "x", username: "" }));
			}
		}
	}, [selectedAccountId, availableAccounts]);

	// Update platform and username when account changes
	useEffect(() => {
		if (selectedAccountId && availableAccounts.length > 0) {
			const selectedAccount = availableAccounts.find(acc => acc._id === selectedAccountId);
			if (selectedAccount) {
				setFormData(prev => ({
					...prev,
					platform: selectedAccount.platform.toLowerCase(),
					username: selectedAccount.handle
				}));
			}
		}
	}, [selectedAccountId, availableAccounts]);

	// Handlers
	const updateFormData = useCallback((updates) => {
		setFormData(prev => ({ ...prev, ...updates }));
	}, []);

	const setClientId = useCallback((clientId) => {
		setSelectedClientId(clientId);
		setSelectedAccountId(null); // Reset account when client changes
	}, []);

	const setAccountId = useCallback((accountId) => {
		setSelectedAccountId(accountId);
	}, []);

	const addKeyword = useCallback((keyword) => {
		const k = keyword.trim();
		if (!k) return;
		// The duplicate check lives inside the updater so it reads the current
		// list. It used to read `formData` captured at mount - a stale closure -
		// so any keyword added after mount could be added again.
		setFormData(prev => (
			prev.customKeywords.includes(k) ? prev : { ...prev, customKeywords: [...prev.customKeywords, k] }
		));
	}, []);

	const removeKeyword = useCallback((keyword) => {
		setFormData(prev => ({
			...prev,
			customKeywords: prev.customKeywords.filter(k => k !== keyword)
		}));
	}, []);

	const toggleCategory = useCallback((category) => {
		setFormData(prev => {
			const enabled = prev.enabledCategories.includes(category);
			return {
				...prev,
				enabledCategories: enabled
					? prev.enabledCategories.filter(c => c !== category)
					: [...prev.enabledCategories, category]
			};
		});
	}, []);

	// Validation
	const validate = useCallback(() => {
		const errors = ScanService.validateScanRequest({
			...formData,
			clientId: selectedClientId,
			accountId: selectedAccountId,
		});
		setValidationErrors(errors);
		return errors.length === 0;
	}, [formData, selectedClientId, selectedAccountId]);

	// Submit scan
	const createScan = useCallback(async () => {
		setError(null);
		setValidationErrors([]);

		// Validate form inline to avoid circular dependency
		const errors = ScanService.validateScanRequest({
			...formData,
			clientId: selectedClientId,
			accountId: selectedAccountId,
		});
		setValidationErrors(errors);

		if (errors.length > 0) {
			return null;
		}

		setLoading(true);

		try {
			const result = await ScanService.createScan({
				...formData,
				clientId: selectedClientId,
				accountId: selectedAccountId,
			});

			return result;
		} catch (err) {
			setError(err.message);
			throw err;
		} finally {
			setLoading(false);
		}
	}, [formData, selectedClientId, selectedAccountId]);

	// Reset form
	const resetForm = useCallback(() => {
		setFormData({
			platform: "x",
			username: "",
			startDate: "",
			endDate: "",
			customKeywords: [],
			sensitivityLevel: "medium",
			enabledCategories: [
				"hate_speech",
				"harassment",
				"explicit",
				"violence",
				"political",
				"cultural",
				"misconduct",
				"drug_use",
				"self_harm",
				"spam",
				"other",
			],
		});
		setSelectedClientId(null);
		setSelectedAccountId(null);
		setError(null);
		setValidationErrors([]);
	}, []);

	// Platform and category options
	const platforms = ScanService.getSupportedPlatforms();
	const sensitivityLevels = ScanService.getSensitivityLevels();
	const riskCategories = ScanService.getRiskCategories();

	// Computed values
	const selectedClient = clients.find(c => c._id === selectedClientId);
	const selectedAccount = availableAccounts.find(a => a._id === selectedAccountId);
	const isFormValid = selectedClientId && selectedAccountId && formData.startDate && formData.endDate;
	const estimatedDuration = formData.startDate && formData.endDate
		? ScanService.estimateScanDuration(formData.platform, {
			startDate: formData.startDate,
			endDate: formData.endDate
		})
		: null;

	return {
		// Form data
		formData,
		selectedClientId,
		selectedAccountId,
		availableAccounts,

		// Loading states
		loading,
		accountsLoading,

		// Error states
		error,
		validationErrors,

		// Data
		clients,
		selectedClient,
		selectedAccount,
		platforms,
		sensitivityLevels,
		riskCategories,

		// Actions
		updateFormData,
		setClientId,
		setAccountId,
		addKeyword,
		removeKeyword,
		toggleCategory,
		createScan,
		resetForm,
		validate,

		// Computed
		isFormValid,
		estimatedDuration,
	};
}; 